import {
  Accordion,
  Button,
  Icon,
  TextField,
  toggleExpandedIndexes,
} from '@monash/portal-react';
import c from './output-form.module.scss';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { createMockUser, MOCK_USER_TYPE } from '@monash/portal-frontend-common';
import { useState } from 'react';
import { useSnackbar } from '../../../../providers/SnackbarProvider';
import InlineErrorMessage from '../../../../common/inline-error-message/InlineErrorMessage';
import classNames from 'classnames';
import { isValidJson } from '../utils';
import MockUserFields from './mock-user-fields/MockUserFields';
import CreationOptions from './creation-options/CreationOptions';

const formatStr = (str) => {
  return JSON.stringify(str, null, 1);
};

const getTextAreaHeight = (str) => {
  const linesCount = str.split(/\r\n|\r|\n/).length;
  return `calc(${linesCount}*1lh)`;
};

const SECTION_ID = {
  MOCK_IDENTITY: 'MOCK_IDENTITY',
  API_RESPONSES: 'API_RESPONSES',
  MOCK_USER_FIELDS: 'MOCK_USER_FIELDS',
  CREATION_OPTIONS: 'CREATION_OPTIONS',
};

const OutputForm = ({
  apiResponses: apiResponsesProp,
  mockUserIdentity: mockUserIdentityProp,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [openAccordionIndexes, setOpenAccordionIndexes] = useState([]);

  const { addSnackbar } = useSnackbar();

  const methods = useForm({
    defaultValues: {
      apiResponses: formatStr(apiResponsesProp),
      mockUserIdentity: formatStr(mockUserIdentityProp),
    },
    shouldUnregister: true,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = methods;

  const onFormSubmitError = (error) => {
    console.error(error);
    const erroredSectionIndexes = [];
    if (error.mockUserIdentity) {
      erroredSectionIndexes.push(
        formSections.findIndex(
          (section) => section.id === SECTION_ID.MOCK_IDENTITY
        )
      );
    }
    if (error.apiResponses) {
      erroredSectionIndexes.push(
        formSections.findIndex(
          (section) => section.id === SECTION_ID.API_RESPONSES
        )
      );
    }
    if (error.mockUserFields) {
      erroredSectionIndexes.push(
        formSections.findIndex(
          (section) => section.id === SECTION_ID.MOCK_USER_FIELDS
        )
      );
    }
    if (error.options) {
      erroredSectionIndexes.push(
        formSections.findIndex(
          (section) => section.id === SECTION_ID.CREATION_OPTIONS
        )
      );
    }
    setOpenAccordionIndexes(erroredSectionIndexes);
  };

  const resolveMockUserIdentity = ({ mockUserIdentity, mockUserFields }) => {
    return {
      ...JSON.parse(mockUserIdentity),
      ...mockUserFields,
    };
  };
  const onDownload = (values) => {
    const { apiResponses, mockUserIdentity, mockUserFields } = values;

    const resolvedMockUserIdentity = resolveMockUserIdentity({
      mockUserIdentity,
      mockUserFields,
    });

    const downloadFile = (fileName, fileContents) => {
      const file = new File([fileContents], fileName);
      const url = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    };
    try {
      downloadFile(
        'mock-user-identity.json',
        formatStr(resolvedMockUserIdentity)
      );
      downloadFile('api-responses.json', apiResponses);
    } catch (error) {
      console.error(error);
      addSnackbar({
        message: 'Data could not be downloaded',
        type: 'error',
      });
    }
  };

  const onSaveMockUser = async (values) => {
    const { apiResponses, mockUserIdentity, mockUserFields, options } = values;

    const resolvedMockUserIdentity = resolveMockUserIdentity({
      mockUserIdentity,
      mockUserFields,
    });

    if (!resolvedMockUserIdentity.mockUserType) {
      resolvedMockUserIdentity.mockUserType = MOCK_USER_TYPE.DEV;
    }

    setIsLoading(true);
    try {
      const newMockUser = await createMockUser({
        identity: resolvedMockUserIdentity,
        apiResponses: JSON.parse(apiResponses),
        options,
      });
      addSnackbar({
        message: `Mock user (UID: ${newMockUser.identity?.uid}) successfully created`,
        type: 'success',
      });
    } catch (error) {
      console.error(error);
      addSnackbar({
        message: 'Mock user could not be created',
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formSections = [
    {
      id: SECTION_ID.MOCK_IDENTITY,
      title: 'Mock User Identity',
      content: (
        <Controller
          name="mockUserIdentity"
          control={control}
          rules={{
            validate: {
              isValidJson,
            },
          }}
          render={({ field: { value, onChange } }) => (
            <div>
              {isEditing ? (
                <TextField
                  value={value}
                  onChange={onChange}
                  placeholder="Enter some JSON here"
                  multiline
                  error={() => errors.mockUserIdentity}
                  errorMsg={errors.mockUserIdentity?.message}
                  height={getTextAreaHeight(value)}
                />
              ) : (
                <>
                  <pre
                    className={classNames(c.printedOutput, {
                      [c.hasError]: errors.mockUserIdentity,
                    })}
                  >
                    {value}
                  </pre>
                  {errors.mockUserIdentity && (
                    <InlineErrorMessage
                      message={errors.mockUserIdentity?.message}
                    />
                  )}
                </>
              )}
            </div>
          )}
        />
      ),
    },
    {
      id: SECTION_ID.API_RESPONSES,
      title: 'API Responses',
      content: (
        <Controller
          name="apiResponses"
          control={control}
          rules={{
            validate: {
              isValidJson,
            },
          }}
          render={({ field: { value, onChange } }) => (
            <div>
              {isEditing ? (
                <TextField
                  value={value}
                  onChange={onChange}
                  placeholder="Enter some JSON here"
                  multiline
                  error={() => errors.apiResponses}
                  errorMsg={errors.apiResponses?.message}
                  height={getTextAreaHeight(value)}
                />
              ) : (
                <>
                  <pre
                    className={classNames(c.printedOutput, {
                      [c.hasError]: errors.apiResponses,
                    })}
                  >
                    {value}
                  </pre>
                  {errors.apiResponses && (
                    <InlineErrorMessage
                      message={errors.apiResponses?.message}
                    />
                  )}
                </>
              )}
            </div>
          )}
        />
      ),
    },
    {
      id: SECTION_ID.MOCK_USER_FIELDS,
      title: 'Mock user fields',
      content: <MockUserFields />,
    },
    {
      id: SECTION_ID.CREATION_OPTIONS,
      title: 'Options',
      content: <CreationOptions />,
    },
  ];

  return (
    <FormProvider {...methods}>
      <form>
        <Button
          onClick={() => {
            setIsEditing((prevIsEditing) => !prevIsEditing);
          }}
          icon={isEditing ? <Icon.Check /> : <Icon.Pencil />}
        >
          {isEditing ? 'Finish editing' : 'Edit'}
        </Button>
        <Accordion
          renderHiddenContent
          expandedIndexes={openAccordionIndexes}
          onClick={(index) =>
            setOpenAccordionIndexes(
              toggleExpandedIndexes(index, openAccordionIndexes)
            )
          }
          items={formSections}
        />
        <div className={c.buttonsContainer}>
          <Button
            onClick={handleSubmit(onDownload, onFormSubmitError)}
            icon={<Icon.FileDownload />}
          >
            Download
          </Button>
          <Button
            onClick={handleSubmit(onSaveMockUser, onFormSubmitError)}
            loading={isLoading}
          >
            Create mock user
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default OutputForm;
